import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Player } from '@shared/types';

// Type utilisateur depuis le backend
interface User {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  spotify_id: string | null;
  level: number;
  xp: number;
  total_points: number;
  games_played: number;
  games_won: number;
  is_premium: boolean;
}

interface AuthState {
  player: Player | null;
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  spotifyToken: string | null;
  spotifyRefreshToken: string | null;
  spotifyExpiresAt: number | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setPlayer: (player: Player) => void;
  setUser: (user: User, token: string) => void;
  setSpotifyTokens: (accessToken: string, refreshToken: string, expiresAt: number) => void;
  logout: () => void;
  checkAuth: () => void;
  refreshSpotifyToken: () => Promise<void>;
  
  // Nouvelles actions pour email/password
  register: (email: string, password: string, name: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  fetchMe: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      player: null,
      user: null,
      token: null,
      isAuthenticated: false,
      spotifyToken: null,
      spotifyRefreshToken: null,
      spotifyExpiresAt: null,
      isLoading: false,
      error: null,
      
      setPlayer: (player) => set({ player, isAuthenticated: true }),
      
      setUser: (user, token) => {
        // Créer le player depuis le user
        const player: Player = {
          id: user.id,
          type: user.spotify_id ? 'spotify' : 'registered',
          spotifyId: user.spotify_id || undefined,
          name: user.name,
          avatar: user.avatar_url,
          level: user.level,
          xp: user.xp,
          connectedAt: Date.now(),
        };
        set({ user, token, player, isAuthenticated: true, error: null });
      },
      
      setSpotifyTokens: (accessToken, refreshToken, expiresAt) => set({
        spotifyToken: accessToken,
        spotifyRefreshToken: refreshToken,
        spotifyExpiresAt: expiresAt,
      }),
      
      logout: () => {
        set({
          player: null,
          user: null,
          token: null,
          isAuthenticated: false,
          spotifyToken: null,
          spotifyRefreshToken: null,
          spotifyExpiresAt: null,
          error: null,
        });
      },
      
      clearError: () => set({ error: null }),
      
      register: async (email, password, name) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/auth/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name }),
          });
          
          const data = await response.json();
          
          if (!response.ok || !data.success) {
            set({ error: data.error || 'Erreur lors de l\'inscription', isLoading: false });
            return false;
          }
          
          get().setUser(data.data.user, data.data.token);
          set({ isLoading: false });
          return true;
          
        } catch (error) {
          set({ error: 'Erreur de connexion au serveur', isLoading: false });
          return false;
        }
      },
      
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          
          const data = await response.json();
          
          if (!response.ok || !data.success) {
            set({ error: data.error || 'Email ou mot de passe incorrect', isLoading: false });
            return false;
          }
          
          get().setUser(data.data.user, data.data.token);
          set({ isLoading: false });
          return true;
          
        } catch (error) {
          set({ error: 'Erreur de connexion au serveur', isLoading: false });
          return false;
        }
      },
      
      fetchMe: async () => {
        const { token } = get();
        if (!token) return;
        
        try {
          const response = await fetch('/auth/me', {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              get().setUser(data.data, token);
            }
          } else {
            // Token invalide, déconnecter
            get().logout();
          }
        } catch (error) {
          console.error('Erreur fetchMe:', error);
        }
      },
      
      checkAuth: () => {
        // Vérifier les paramètres URL (callback OAuth Spotify/Google)
        const params = new URLSearchParams(window.location.search);
        
        // Gestion du callback Google OAuth
        const authToken = params.get('auth_token');
        const authProvider = params.get('auth_provider');
        
        if (authToken && authProvider === 'google') {
          // Récupérer les infos utilisateur avec le token
          fetch('/auth/me', {
            headers: { 'Authorization': `Bearer ${authToken}` }
          })
          .then(res => res.json())
          .then(data => {
            if (data.success) {
              get().setUser(data.data, authToken);
            }
          })
          .catch(err => console.error('Erreur auth Google:', err));
          
          // Nettoyer l'URL
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        
        // Gestion du lien Spotify depuis le profil
        if (params.get('spotify_linked') === 'true') {
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          const expiresAt = params.get('expires_at');
          
          if (accessToken && refreshToken && expiresAt) {
            set({
              spotifyToken: accessToken,
              spotifyRefreshToken: refreshToken,
              spotifyExpiresAt: parseInt(expiresAt),
            });
            
            // Rafraîchir les infos utilisateur
            get().fetchMe();
          }
          
          // Nettoyer l'URL
          window.history.replaceState({}, '', window.location.pathname);
        }
        
        // Vérifier si on a un token et rafraîchir les infos
        const { token } = get();
        if (token) {
          get().fetchMe();
        }
        
        // Vérifier si le token Spotify a expiré ou expire bientôt
        const { spotifyExpiresAt, spotifyRefreshToken } = get();
        if (spotifyExpiresAt && spotifyRefreshToken && Date.now() > spotifyExpiresAt - 60000) {
          console.log('🔄 Token Spotify expiré ou expire bientôt, rafraîchissement...');
          get().refreshSpotifyToken();
        }
      },
      
      refreshSpotifyToken: async () => {
        const { spotifyRefreshToken } = get();
        if (!spotifyRefreshToken) return;
        
        console.log('🔄 Rafraîchissement du token Spotify...');
        
        try {
          const response = await fetch('/auth/spotify/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: spotifyRefreshToken }),
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Token Spotify rafraîchi');
            set({
              spotifyToken: data.access_token,
              spotifyRefreshToken: data.refresh_token,
              spotifyExpiresAt: data.expires_at,
            });
          } else {
            console.error('❌ Échec refresh token Spotify');
            // Token invalide, supprimer les tokens Spotify (mais garder la session)
            set({
              spotifyToken: null,
              spotifyRefreshToken: null,
              spotifyExpiresAt: null,
            });
          }
        } catch (error) {
          console.error('Erreur refresh token:', error);
        }
      },
    }),
    {
      name: 'blindtest-auth',
      partialize: (state) => ({
        player: state.player,
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        spotifyToken: state.spotifyToken,
        spotifyRefreshToken: state.spotifyRefreshToken,
        spotifyExpiresAt: state.spotifyExpiresAt,
      }),
    }
  )
);
